import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { WORLD_CUP_START, WORLD_CUP_OFFICIAL_LOGO } from "@/lib/worldCup2026";

const KICKOFF = new Date(`${WORLD_CUP_START}T20:00:00Z`).getTime();
const STORAGE_KEY = "wc26-bar-dismissed-at";
const REAPPEAR_AFTER_MS = 24 * 60 * 60 * 1000;

function diff() {
  const ms = KICKOFF - Date.now();
  if (ms <= 0) return null;
  return {
    d: Math.floor(ms / 86400000),
    h: Math.floor((ms % 86400000) / 3600000),
    m: Math.floor((ms % 3600000) / 60000),
  };
}

export function WorldCupCountdownBar() {
  const navigate = useNavigate();
  const [cd, setCd] = useState(() => diff());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const raw = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (raw) {
      const ts = Number(raw);
      if (!Number.isNaN(ts) && Date.now() - ts < REAPPEAR_AFTER_MS) {
        setDismissed(true);
      }
    }
  }, []);

  useEffect(() => {
    const t = setInterval(() => setCd(diff()), 30000);
    return () => clearInterval(t);
  }, []);

  if (!cd || dismissed) return null;

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setDismissed(true);
  };

  const go = () => navigate("/world-cup");

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && go()}
      className="relative w-full h-9 flex items-center justify-center gap-3 px-4 cursor-pointer border-b border-primary/20 bg-card overflow-hidden group"
      style={{
        backgroundImage:
          "linear-gradient(90deg, rgba(255,0,0,0.10) 0%, rgba(60,59,110,0.10) 50%, rgba(0,104,71,0.10) 100%)",
      }}
      aria-label="FIFA World Cup 2026 countdown"
    >
      <img
        src={WORLD_CUP_OFFICIAL_LOGO}
        alt=""
        className="h-5 w-auto flex-shrink-0 opacity-95"
        loading="lazy"
      />
      <span
        className="hidden sm:inline text-[11px] uppercase tracking-[0.18em] font-bold text-white/90 whitespace-nowrap"
        style={{ fontFamily: "Surgena, sans-serif", color: "rgba(255,200,50,0.85)" }}
      >
        The World Plays in 3 Homes
      </span>
      <span className="hidden sm:inline text-[hsl(0_0%_35%)] text-[10px]" aria-hidden>
        ◆
      </span>
      <span className="inline-flex items-baseline gap-1 tabular-nums font-black text-[13px] whitespace-nowrap" style={{ color: "rgba(255,200,50,0.95)" }}>
        <span>{String(cd.d).padStart(2, "0")}</span><span className="text-[10px] opacity-70 mr-1">D</span>
        <span>{String(cd.h).padStart(2, "0")}</span><span className="text-[10px] opacity-70 mr-1">H</span>
        <span>{String(cd.m).padStart(2, "0")}</span><span className="text-[10px] opacity-70">M</span>
      </span>
      <span className="hidden md:inline text-[11px] font-semibold text-white/80 group-hover:text-white transition-colors">
        Explore →
      </span>
      <button
        onClick={handleDismiss}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-white/10 text-white/60 hover:text-white/90 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
